import { routesDefinitions } from "lib/actions/routeDefinitions";
import { getCesiumViewableVersions } from "lib/hooks/layers/useLayersControls";
import { useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useProjectLayers } from "lib/hooks/layers/useProjectLayers";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useSyncCesium } from "components/elements/CesiumViewer/core/useSyncCesium";
import { useViewsMode } from "lib/contexts/SpatialContexts/ViewsModeContext";

/**
 * Applies layer rules, and suppresses the global Search tool while the user
 * is on a layer's criteria or criteria list page.
 */
export function useLayersCriteriaSync() {
  const router = useRouter();
  const projectId = useCurrentProjectId();
  const { layers } = useProjectLayers(projectId);
  const {
    assetsPlugin,
    assetsPropertiesSearchToolPlugin,
    assetsPropertiesLayerCriteriaPlugin,
  } = useCesium();
  const visibleLayerIds = useSyncCesium(assetsPlugin.layers);
  const { loadedView } = useViewsMode();

  const isOnLayerCriteriaPage = routesDefinitions.layerCriteria.regex.test(
    router.asPath,
  );

  const layerRuleSets = useMemo(
    () =>
      (layers ?? [])
        .filter((layer) => visibleLayerIds.has(layer.id))
        .map((layer) => ({
          layerId: layer.id,
          tilesetIds: getCesiumViewableVersions(layer).map((v) => v.contentUri),
          rules: layer.searchesStateJson?.searches ?? [],
        })),
    [layers, visibleLayerIds],
  );

  useEffect(() => {
    assetsPropertiesSearchToolPlugin.setSuppressed(isOnLayerCriteriaPage);
  }, [assetsPropertiesSearchToolPlugin, isOnLayerCriteriaPage]);

  useEffect(() => {
    if (loadedView) {
      // Tricky bit:
      // Apply captured layer rules from when the view was created.
      // Also apply live rules for any layers the user enables on top.
      // View layer rules use negative synthetic layerIds to avoid collisions.
      const viewLayers = loadedView.toolsStateJson?.layerSearches?.layers ?? [];
      const viewLayerRuleSets = viewLayers.map((entry, index) => ({
        layerId: -(index + 1),
        tilesetIds: entry.contentUris,
        rules: entry.searches,
      }));
      assetsPropertiesLayerCriteriaPlugin.setRuleSets([
        ...viewLayerRuleSets,
        ...layerRuleSets,
      ]);
    } else {
      assetsPropertiesLayerCriteriaPlugin.setRuleSets(layerRuleSets);
    }
  }, [assetsPropertiesLayerCriteriaPlugin, loadedView, layerRuleSets]);

  useEffect(
    () => () => {
      assetsPropertiesSearchToolPlugin.setSuppressed(false);
      assetsPropertiesLayerCriteriaPlugin.setRuleSets([]);
    },
    [assetsPropertiesSearchToolPlugin, assetsPropertiesLayerCriteriaPlugin],
  );
}
